import {
    assest_management, frameworkComplianceService, scan_alert_risk_data, scanner_domain_results,
} from '../services';

const BAND_WEIGHT = { critical: 4, high: 3, medium: 2, low: 1 };
const TOP_RISKS = 5;

const payload = (res) => {
    const body = res?.statusCode?.response_data;
    if (body?.data === undefined || body?.data === null) throw new Error('No data');
    return body.data;
};

const upper = (value) => (value ? String(value).toUpperCase() : null);

export function toPosture(res) {
    const data = payload(res);
    const categories = (Array.isArray(data.categories) ? data.categories : []).map((item) => ({
        category: item.category,
        grade: upper(item.avg_grade),
        high: item.severity_counts?.high ?? 0,
        medium: item.severity_counts?.medium ?? 0,
        low: item.severity_counts?.low ?? 0,
    }));
    return { grade: upper(data.overall_grade), scanned: Boolean(data.is_scanned), categories };
}

export function toAssets(res) {
    if (res?.statusCode?.status === 0) return null;
    const data = payload(res);
    const total = (list) => (Array.isArray(list) ? list.reduce((sum, item) => sum + (Number(item?.count) || 0), 0) : 0);
    return total(data.ExternalAssetInventory) + total(data.InternalAssetInventory);
}

const bandOf = (value) => (typeof value === 'string' ? value.split(' - ')[0].trim().toLowerCase() : '');

const titleOf = (row) => {
    const statement = row['Risk Statement'];
    if (typeof statement === 'string' && statement.trim()) return statement.trim();
    const fallback = Object.entries(row).find(([key, value]) => !['Risk Severity', 'Risk Band', 'Risk Category'].includes(key) && typeof value === 'string' && value.trim());
    return fallback ? fallback[1].trim() : '';
};

export function toRisks(res) {
    const records = payload(res)?.records;
    if (!Array.isArray(records)) throw new Error('No data');
    const rows = records.find((record) => Array.isArray(record?.ai_response) && record.ai_response.length > 0)?.ai_response || [];
    const risks = rows
        // The live scan-alert API returns the band under `Risk Severity`
        // (e.g. "High"); older payloads (and the NmtAI table) call the same
        // field `Risk Band` (e.g. "High - act now"). Read whichever is present
        // — reading only the old key left every pill rendering as "Unrated".
        .map((row, index) => ({ id: index, severity: bandOf(row['Risk Severity'] ?? row['Risk Band']), title: titleOf(row) }))
        .filter((risk) => risk.title);
    const weight = (risk) => BAND_WEIGHT[risk.severity] || 0;
    return {
        total: risks.length,
        high: risks.filter((risk) => weight(risk) >= BAND_WEIGHT.high).length,
        top: [...risks].sort((a, b) => weight(b) - weight(a)).slice(0, TOP_RISKS),
    };
}

export function toFrameworks(res) {
    const list = payload(res)?.frameworks;
    return (Array.isArray(list) ? list : []).map((item) => ({
        id: item.framework_id,
        name: item.name,
        compliant: item.reviewer_compliance_percentage ?? 0,
        responses: item.user_compliance_percentage ?? 0,
    }));
}

export function buildInsights({ posture, assets, risks, frameworks }) {
    return {
        summary: {
            grade: posture?.grade ?? null,
            highRisks: risks ? risks.high : null,
            assets,
        },
        scanned: posture ? posture.scanned : null,
        categories: posture ? posture.categories : null,
        risks,
        frameworks,
    };
}

const settle = async (call, map) => map(await call());

export async function loadInsights() {
    const results = await Promise.allSettled([
        settle(scanner_domain_results, toPosture),
        settle(assest_management, toAssets),
        settle(() => scan_alert_risk_data({ organization_id: localStorage.getItem('organization_id') }), toRisks),
        settle(frameworkComplianceService, toFrameworks),
    ]);
    const [posture, assets, risks, frameworks] = results.map((result) => (result.status === 'fulfilled' ? result.value : null));
    if ([posture, assets, risks, frameworks].every((value) => value === null)) throw new Error('Insights unavailable');
    return buildInsights({ posture, assets, risks, frameworks });
}
